
import { Component, Vue } from 'vue-property-decorator'

import BacHeader from '@/templates/BacHeader.vue'
import BacAlert from '@/components/BacAlert.vue'
import BacInput from '@/components/BacInput.vue'
import BacButton from '@/components/BacButton.vue'

import { ShopDonate } from '../../lib/models/ShopDonate'
import { Team } from '../../lib/models/Team'
import { Http } from '../../lib/utils/Http'

import { loadScript, PayPalNamespace } from '@paypal/paypal-js'
import { Country } from '@/lib/models/Country'

@Component({
  components: {
    BacHeader,
    BacAlert,
    BacInput,
    BacButton
  }
})
export default class Donate extends Vue {
  shopDonate: ShopDonate = new ShopDonate()
  team: Team = new Team()

  amount = '50.00'
  firstname = ''
  lastname = ''
  email = ''
  birthday = ''
  message = ''

  donateAs = 'private'

  companyName = ''

  street = ''
  streetnumber = ''
  zip = ''
  city = ''
  countryId = -1

  countries: Country[] = [];

  isSaving = false
  validateErrors: string[] = []

  paypal: PayPalNamespace | null = null
  finished = false
  hasMajorError = false

  mounted () {
    this.loadShopDonate(parseInt(this.$route.params.shopDonateId))
    this.loadTeam(parseInt(this.$route.params.teamId))

    this.loadPaypal()
    this.loadCountries()
  }

  async loadCountries () {
    let countryData = null
    const countriesFromLocalStorage = localStorage.getItem('countries')
    const countriesJson = countriesFromLocalStorage
      ? JSON.parse(countriesFromLocalStorage)
      : null
    if (!countriesJson || Date.now() - 3600000 >= countriesJson.time) {
      countryData = await (await Http.request('/api/country')).json()
      localStorage.setItem(
        'countries',
        JSON.stringify({ time: Date.now(), countries: countryData })
      )
    } else {
      countryData = countriesJson.countries
    }

    countryData.forEach((country: any) => {
      const newCountry = new Country()
      newCountry.id = country.country_id
      newCountry.name = country.name
      newCountry.isoCode2 = country.iso_code_2
      this.countries.push(newCountry)
    })
  }

  get paypalClientId () {
    return process.env.NODE_ENV === 'development' ? 'AcXtzRIghi-frsxRY0ccGy1zODhjkxl2-ILvQNQTc_cJXv2RBs-ICHUAwRIsGLqQjST19w6l5d2IP5Ob' : 'AZltsBooxR6ZuawbFF-heBkD0JCIOorZAIxvpIx4S--ZFZvYWx5JUs9VjZ2dSMoBkXtdP8pEft_zj_5Y'
  }

  async loadPaypal () {
    try {
      this.paypal = await loadScript({ clientId: this.paypalClientId, currency: 'EUR', disableFunding: 'eps' })
    } catch (error) {
      console.error('failed to load the PayPal JS SDK script', error)
    }

    if (this.paypal) {
      try {
        await (this.paypal as any).Buttons({
          createOrder: async (data: any, actions: any) => {
            const resData = await this.saveAndContinue()

            if (!resData.orderId) {
              this.validateErrors.push('Ein unbekannter Fehler ist aufgetreten, bitte kontaktiere uns unter hello@backroadclub.com')
              console.error('failed creating order, this must never happen!!')
            }
            const res = await Http.request(`/api/paypal/order/${resData.orderId}`, {
              method: 'post',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                amount: this.amount,
                email: this.shopDonate.paypalEmail
              })
            })
            return (await res.json()).paypal_id
          },

          onApprove: async (data: any, actions: any) => {
            return actions.order.capture().then((details: any) => {
              Http.request('/api/paypal/order/check').then(() => {
                this.finished = true
              })
            })
          }
        })
          .render('#paypal-buttons')
      } catch (error) {
        console.error('failed to render the PayPal Buttons', error)
      }
    }
  }

  async loadShopDonate (shopDonateId: number) {
    try {
      this.shopDonate = await ShopDonate.get(shopDonateId)
    } catch (ex) {
      this.hasMajorError = true
    }
  }

  async loadTeam (teamId: number) {
    try {
      this.team = await Team.get(teamId)
    } catch (ex) {
      this.hasMajorError = true
    }
  }

  validate (): string[] {
    const errors = []

    if (this.donateAs === 'private') {
      if (!this.firstname) {
        errors.push('Bitte gib deinen Vornamen ein')
      }
      if (!this.lastname) {
        errors.push('Bitte gib deinen Nachnamen ein')
      }
    } else {
      if (!this.companyName) {
        errors.push('Bitte gib deinen Firmennamen ein')
      }
    }
    errors.push(...this.validateAddress())
    if (!this.email) {
      errors.push('Bitte gib deine E-Mail ein')
    }
    if (!this.amount || !parseFloat(this.amount)) {
      errors.push('Bitte gib eine Zahl als Spende an')
    }
    return errors
  }

  validateAddress (): string[] {
    const errors = []
    if (!this.street) {
      errors.push('Bitte gib deine Straße ein!')
    }
    if (!this.streetnumber) {
      errors.push('Bitte gib deine Hausnummer ein!')
    }
    if (!this.zip) {
      errors.push('Bitte gib deine Postleitzahl ein!')
    }
    if (!this.city) {
      errors.push('Bitte gib deinen Ort ein!')
    }
    if (!this.countryId || this.countryId < 0) {
      errors.push('Bitte wähle dein Land aus!')
    }
    return errors
  }

  async saveAndContinue () {
    if (this.isSaving) {
      return
    }
    this.validateErrors = this.validate()
    if (this.validateErrors.length) {
      return
    }

    const res = await Http.request('/api/shop-donate-order', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        shopDonateId: this.shopDonate.id,
        type: this.donateAs,
        companyName: this.companyName,
        email: this.email,
        paypalEmail: this.shopDonate.paypalEmail,
        firstname: this.firstname,
        lastname: this.lastname,
        price: this.amount,
        teamId: this.team.id,
        birthday: this.birthday,
        token: 'c3a82f9407ef797ecd91c2de116130a1', // refactor some day, this should not be needed and was for legacy compability
        message: this.message,
        address: {
          street: this.street,
          streetnumber: this.streetnumber,
          zip: this.zip,
          city: this.city,
          countryId: this.countryId
        }
      })
    })

    if (res.status !== 200) {
      this.validateErrors.push('Es gab einen Fehler beim durchführen der Zahlung, bitte kontaktiere uns unter hello@backroadclub.com!')
    }

    // everything good, continue to paypal
    // PROD: https://ipnpb.paypal.com/cgi-bin/webscr
    // DEV: https://ipnpb.sandbox.paypal.com/cgi-bin/webscr

    this.isSaving = false
    const resData = await res.json()
    if (resData && resData.shopDonateOrder.id) {
      return { orderId: resData.shopDonateOrder.id }
    } else {
      return resData
    }
  }
}
